import { Info } from '@mui/icons-material';
import { Box, Chip, Grow, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import { SubprojectBuildingDetails } from '../../stores/SubprojectStore.ts';

interface AirExchangeFactorProps {
  subproject?: SubprojectBuildingDetails;
  onUpdate: React.Dispatch<
    React.SetStateAction<SubprojectBuildingDetails | undefined>
  >;
  readOnly?: boolean;
}

export const AirExchangeFactor: React.FC<AirExchangeFactorProps> = ({
  subproject,
  onUpdate,
  readOnly,
}) => {
  const airExchangeFactors = [
    'Typical Use',
    'Classroom, Library, Museum',
    'Dining Facility, Lab, Gym',
    'Pools and Ice Rinks',
  ] as const;
  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
        }}
      >
        <Typography
          variant={'h5'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mt: 2,
            textTransform: 'uppercase',
            fontSize: 16,
            letterSpacing: 8,
          }}
        >
          What&apos;s the Air Exchange Factor?{' '}
          <Tooltip
            arrow
            leaveDelay={200}
            title={
              <React.Fragment>
                <Typography variant={'body2'}>
                  <strong>Air Exchange Factor</strong> dictates how frequently
                  air will need to be cycled through the building and impacts
                  the level of energy required to power the HVAC system
                </Typography>
              </React.Fragment>
            }
          >
            <Info fontSize={'inherit'} />
          </Tooltip>
        </Typography>
        <Typography
          variant={'caption'}
          sx={{
            m: 2,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          Select one
        </Typography>
      </Box>
      <Box
        component={TransitionGroup}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          paddingTop: 4,
          paddingBottom: 2,
          gap: 2,
          justifyContent: 'center',
          viewTransitionName: 'ease-in',
          '.MuiChip-icon': {
            height: 40,
            width: 40,
            margin: [0, 0, 0, 1],
            flexShrink: 0,
            flexGrow: 0,
            fontWeight: 'bold',
            textAlign: 'center',
            borderRadius: '100%',
            backgroundColor: 'background.default',
            color: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '.MuiChip-label': {
            flexGrow: 1,
            textAlign: 'center',
          },
        }}
      >
        {airExchangeFactors.map((airExchangeFactor, idx) => (
          <Grow key={idx} timeout={300 + idx * Math.random() * 150}>
            <Chip
              icon={<Box sx={{ fontSize: 'x-large' }}>{idx + 1}</Box>}
              color={
                subproject?.airExchangeFactor === idx + 1 ? 'secondary' : 'info'
              }
              variant={'filled'}
              sx={{
                '&:hover': {
                  backgroundColor: 'success.main', // example — customize as needed
                },
                height: 48,
                width: 270,
                borderRadius: 48,
                cursor: 'pointer',
                m: 1,
                pt: 1,
                pb: 1,
                pr: 1,
              }}
              onClick={() => {
                if (readOnly) return;
                if (!subproject) {
                  return;
                }

                const updated = structuredClone(subproject);
                updated.airExchangeFactor = idx + 1;
                onUpdate(updated);
              }}
              label={
                <Typography
                  variant={'subtitle2'}
                  sx={{
                    textAlign: 'left', // Left-align text
                    flexGrow: 1, // Ensures text expands to fill space
                  }}
                >
                  {airExchangeFactor}
                </Typography>
              }
            />
          </Grow>
        ))}
      </Box>
    </React.Fragment>
  );
};
